import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import type { BitriseSetupStep } from '@/api/schemas';
import { STEP_LOOK, activeStepIndex, showsStepDetail, stepLabelMuted } from './bitriseModel';

function StepIcon({ status, isRunning }: { status: BitriseSetupStep['status']; isRunning: boolean }) {
  if (isRunning) return <Icon name="refresh" size={16} className="mt-0.5 shrink-0 animate-spin text-primary motion-reduce:animate-none" />;
  const look = STEP_LOOK[status];
  return <Icon name={look.icon} size={16} className={cn('mt-0.5 shrink-0', look.tone)} />;
}

function StepText({ step, isRunning }: { step: BitriseSetupStep; isRunning: boolean }) {
  return (
    <div className="min-w-0">
      <span className={cn('font-medium', stepLabelMuted(step.status, isRunning) ? 'text-muted-foreground' : 'text-foreground')}>{step.label}</span>
      {step.status === 'skipped' ? <span className="text-subtle"> · not needed</span> : null}
      {showsStepDetail(step) ? <span className="block break-words text-xs text-subtle">{step.detail}</span> : null}
      {step.error ? <span className="block break-words text-xs text-failed">{step.error}</span> : null}
      {step.fix ? <span className="block break-words text-xs text-muted-foreground">{step.fix}</span> : null}
    </div>
  );
}

/** The five recorded steps of "Create on Bitrise": what is done, what is running, and what failed with how to fix it. */
export function BitriseSteps({ steps, running }: { steps: BitriseSetupStep[]; running?: boolean }) {
  const active = activeStepIndex(steps, running);
  return (
    <ol data-testid="bitrise-steps" className="flex flex-col gap-1.5">
      {steps.map((s, i) => (
        <li key={s.key} data-step={s.key} data-status={i === active ? 'running' : s.status} className="flex items-start gap-2 text-[13px]">
          <StepIcon status={s.status} isRunning={i === active} />
          <StepText step={s} isRunning={i === active} />
        </li>
      ))}
    </ol>
  );
}
